import axios from 'axios';
import { API_BASE_URL, DOCUMENTS_API_URL, LOCAL_API_URL } from '../constants/api';
import { getCurrentUserId } from '../../../utils/userUtils';

export const documentApiService = {
  fetchDocuments: async (userId) => {
    const response = await axios.get(`${API_BASE_URL}/api/list-files/`, {
      params: { userId: userId },
    });
    
    const fetchedDocuments = response.data.files.map((file, index) => {
      const fileName = typeof file === 'string' ? file : file.name || '';
      return {
        id: index + 1,
        name: fileName,
        company: file.company_name || 'Unspecified Company',
        type: file.document_type || 'Unspecified Type',
        uploadDate: file.upload_date || new Date().toISOString().split('T')[0],
      };
    });
    
    return fetchedDocuments;
  },

  uploadDocument: async (file, uploadFileName, uploadCompany, uploadType, skipEmbeddings = true) => {
    const userId = getCurrentUserId();
    const originalExtension = file.name.slice(file.name.lastIndexOf('.'));
    const effectiveFileName = uploadFileName.trim()
      ? (uploadFileName.endsWith(originalExtension) ? uploadFileName : uploadFileName + originalExtension)
      : file.name;

    const newFile = new File([file], effectiveFileName, { type: file.type });
    
    const formData = new FormData();
    formData.append('file', newFile);
    formData.append('user_id', userId);
    formData.append('company_name', uploadCompany);
    formData.append('document_type', uploadType);
    formData.append('skip_embeddings', skipEmbeddings);

    const response = await axios.post(`${DOCUMENTS_API_URL}/api/upload-file/`, formData);
    return response.data;
  },

  deleteDocument: async (documentName) => {
    const userId = getCurrentUserId();
    const encodedFileName = encodeURIComponent(documentName);
    await axios.delete(`${LOCAL_API_URL}/api/delete-file/${userId}/${encodedFileName}`);
  },

  downloadDocument: async (document) => {
    const userId = getCurrentUserId();
    const encodedFileName = encodeURIComponent(document.name);
    const downloadUrl = `${API_BASE_URL}/api/download-file/?file_name=${encodedFileName}&user_id=${userId}`;
    window.open(downloadUrl, '_blank');
  },

  getPresignedUrl: async (fileName) => {
    const userId = getCurrentUserId();
    const response = await axios.get(`${API_BASE_URL}/api/getPresignedUrl`, {
      params: { fileName, userId },
    });
    return response.data.url;
  },

  convertToPdf: async (fileName) => {
    const userId = getCurrentUserId();
    const response = await axios.get(`${API_BASE_URL}/api/convert-to-pdf/`, {
      params: { fileName, userId },
    });
    return response.data.pdfUrl;
  },

  updateDocumentMetadata: async (documentName, newFileName, companyName, documentType) => {
    const userId = getCurrentUserId();
    const response = await axios.patch(
      `${API_BASE_URL}/api/update-document-metadata/${userId}/${documentName}`,
      {
        new_filename: newFileName,
        company_name: companyName,
        document_type: documentType
      }
    );
    return response.data;
  },

  queryChatbot: async (filename, question, conversationHistory = [], useGeminiDirect = true) => {
    const userId = getCurrentUserId();
    const endpoint = useGeminiDirect ? '/api/chatbot-gemini/' : '/api/chatbot/';
    const response = await axios.post(`${API_BASE_URL}${endpoint}`, {
      user_id: userId,
      question,
      document_name: filename,
      conversation_history: conversationHistory
    });
    return response.data;
  },
  
  queryChatbotStream: async (filename, question, conversationHistory = [], onChunk) => {
    const userId = getCurrentUserId();
    const endpoint = '/api/chatbot-gemini-stream/';
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        question,
        document_name: filename,
        conversation_history: conversationHistory
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6);
          if (jsonStr.trim()) {
            try {
              const data = JSON.parse(jsonStr);
              console.log('Received chunk:', data.type, data.content?.length || 0);
              onChunk(data);
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    }
  }
};